import {
  createContext,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
  type RefObject,
} from "react";
import type { FrameType } from "@/api/spaces";
import type { ContextMenuAnchor } from "@/components/ui/context-menu";
import type { FrameData } from "@/lib/frame-data";
import type { FrameChromeMenuItem } from "./frame-chrome-config";
import type { FrameProps } from "./frame-props";

/** How the surface shows frame `id` right now. */
export interface FrameChromeState {
  /** Shown as the focused window (canvas Focus, or a floating window). */
  focused: boolean;
  maximized: boolean;
  /** Collapsed to its title in the conversation companion stack. */
  stackCollapsed: boolean;
  /** Pushed out of the conversation companion stack's visible titles. */
  stackOverflowHidden: boolean;
  /** Hidden titles summarised on this collapsed stack title. */
  overflowTitleCount?: number;
  /** A read-only continuation of a window owned by another display. */
  preview: boolean;
  /**
   * ← would repeat × and the centred Back pill (a one-item preview above
   * the wall), so the pill leaves it out.
   */
  backHidden?: boolean;
}

/** A frame whose top edge is closer than this to its surface's top draws its
 *  hover chrome inside instead of above. */
export const FRAME_CHROME_INSIDE_TOP = 40;

/** Where the hover chrome sits and how much it scales against the surface. */
export interface FrameChromeGeometry {
  inside: boolean;
  scale: number;
}

/** An opened item's menu: its type extras lead the shared card menu. */
export interface FrameItemMenuProps {
  frame: FrameData;
  extras: readonly FrameChromeMenuItem[];
}

/** The same menu opened by a right-click, plus Copy selection. */
export interface FrameItemContextMenuProps extends FrameItemMenuProps {
  /** Where it opens; `null` closes it. */
  anchor: ContextMenuAnchor | null;
  /** The text selected where the right-click landed. */
  selection: string;
  frameRoot: () => HTMLElement | null;
  onClose: () => void;
}

/**
 * A surface that owns its frames' right-click: the ⋯ model (opened items)
 * or the wall's card menu (Pinned tiles).
 */
export interface FrameContextMenuOwner {
  /**
   * Whether a right-click gets a menu; without an item the click passes
   * through instead of opening nothing.
   */
  opens(
    frame: FrameData,
    extras: readonly FrameChromeMenuItem[],
    selection: string,
  ): boolean;
  render(props: FrameItemContextMenuProps): ReactNode;
}

export interface FrameControlsProps {
  frameId: string;
  frameType: FrameType;
  visible: boolean;
}

/**
 * What a surface (canvas, floating windows, any plain container) tells the
 * shared frame chrome. Renderers are module constants, so the hooks they
 * expose keep a stable call order for every frame they render.
 */
export interface FrameRenderer {
  useChromeState(id: string): FrameChromeState;
  useChromeGeometry(
    id: string,
    rootRef: RefObject<HTMLDivElement | null>,
  ): FrameChromeGeometry;
  /** Extra backing-store scale for canvases drawn inside a frame. */
  useBackingScale(): number;
  /**
   * Set by surfaces where every frame is its own closable window (desktop
   * floating frames). Such frames are always focused and never belong to
   * the canvas presentation.
   */
  closeFrame?: (id: string) => void;
  /** Surface-owned controls drawn over the frame (canvas resize handles). */
  renderControls?: (props: FrameControlsProps) => ReactNode;
  /** Lets the surface swap the live body (canvas detail level). */
  renderBody?: (props: FrameProps, body: ReactNode) => ReactNode;
  /**
   * Surface-owned window controls after the hairline: the opened item's ⋯
   * and its Focus or Maximize button.
   */
  renderMenu?: (props: FrameItemMenuProps) => ReactNode;
  /** Surface-owned right-click menu; absent, the canvas frame menu. */
  contextMenu?: FrameContextMenuOwner;
  /**
   * The frame body's material: glass (default) or solid paper/ink, which
   * draws no native glass plate.
   */
  material?: "glass" | "solid";
  /**
   * Frames are windows that own their geometry: the body never writes the
   * canvas node (a chat's input expansion).
   */
  independentSurface?: boolean;
  /**
   * `none`: the surface owns the frame's chrome (the wall's Pinned tiles):
   * no hover chrome but the attention dot, no toolbar tools, and a frame's
   * top row is only its calm title line. Default `full`.
   */
  chrome?: "full" | "none";
}

const PLAIN_CHROME_STATE: FrameChromeState = {
  focused: false,
  maximized: false,
  stackCollapsed: false,
  stackOverflowHidden: false,
  preview: false,
};
const PLAIN_CHROME_GEOMETRY: FrameChromeGeometry = { inside: true, scale: 1 };

/** Outside React Flow a frame is always shown full, at scale 1. */
export const plainFrameRenderer: FrameRenderer = {
  useChromeState: () => PLAIN_CHROME_STATE,
  useChromeGeometry: () => PLAIN_CHROME_GEOMETRY,
  useBackingScale: () => 1,
};

export const FrameRendererContext =
  createContext<FrameRenderer>(plainFrameRenderer);

/** Whether the surface draws this frame without its own chrome. */
export function useFrameChromeHidden(): boolean {
  return useContext(FrameRendererContext).chrome === "none";
}

/** The renderer of the surface this frame is mounted in. */
export function useFrameRenderer(): FrameRenderer {
  const renderer = useContext(FrameRendererContext);
  const mountedRenderer = useRef(renderer);
  useEffect(() => {
    if (import.meta.env.DEV && mountedRenderer.current !== renderer) {
      console.warn(
        "[frame-renderer] renderer changed under a mounted frame; its hooks may run out of order",
      );
    }
  }, [renderer]);
  return renderer;
}
